import { useMutation, useQuery } from '@apollo/client';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { padRoutineId } from '../../../../../../Utils/dbSettingsUtils';
import { rutinInfos } from '../../../../_Common/Const/RutinInfo';
import { IStandardRutinWrapperProps } from '../../../../_StandardRutin/StandardRutin';
import {
  updateKalenderDBSettings,
  updateKalenderDBSettingsVariables,
} from '../Mutations/UpdateKalenderDBSettingsMutation/_QueryTypes/updateKalenderDBSettings';
import kalenderDBSettingsQuery from '../Queries/KalenderDBSettingsQuery/KalenderDBSettingsQuery.gql';
import {
  KalenderDBSettingsQuery,
  KalenderDBSettingsQueryVariables,
  KalenderDBSettingsQuery_me_dbsettings_values_value_TEDMixedBoolean,
  KalenderDBSettingsQuery_me_dbsettings_values_value_TEDMixedString,
} from '../Queries/KalenderDBSettingsQuery/_QueryTypes/KalenderDBSettingsQuery';
import { ViewDbSettingsContextProvider } from './SchedulerComponent/ViewDbSettingsContext';
import updateKalenderDBSettingsMutation from '../Mutations/UpdateKalenderDBSettingsMutation/updateKalenderDBSettings.gql';
import { INyAktivitetInitialValues } from './SchedulerComponent/Utils/NyAktivitetInitialValues';
import { IResourceInfo } from '../Utils/KalenderTypes';
import { ResursTypContextProvider } from '../../../Contexts/ResursTypContext/ResursTypContext';
import {
  defaultKalenderProps,
  makeSelection,
} from './KalenderCustomUrvalButton';
import { LoginStateContext } from '../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import { ICalendarSelection } from '../../../../../../BaseComponents/CalendarView/Utils/EDCalendarViewUtils';
import kalenderInitialUrvalQuery from '../Queries/KalenderInitialUrvalQuery/KalenderInitialUrvalQuery.gql';
import {
  KalenderInitialUrvalQuery,
  KalenderInitialUrvalQueryVariables,
} from '../Queries/KalenderInitialUrvalQuery/_QueryTypes/KalenderInitialUrvalQuery';
import { RutinSettingsContext } from '../../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { convertUrval } from '../../../../../../Components/Urval/Components/SavedUrval/Utils/convertSavedUrval';
import { getCheckedResourcesFromUrval } from './KalenderResourceTreeSelect';
import { convertResourceIDsToResourceInfos } from './KalenderResourceSelectDialog';
import { KodplanContext } from '../../../../../../Components/KodplanManager/Context/KodplanContext';
import Logger from '../../../../../../Utils/Logger';
import {
  AvenySYSOptionQuery,
  AvenySYSOptionQueryVariables,
} from '../../../../../../Components/QueryComponents/AvenySYSOptionQuery/_QueryTypes/AvenySYSOptionQuery';
import optionQuery from '../../../../../../Components/QueryComponents/AvenySYSOptionQuery/AvenySYSOptionQuery.gql';

const KalenderWrapper: React.FC<
  React.PropsWithChildren<IStandardRutinWrapperProps>
> = props => {
  const { setUrval } = props;
  const location = useLocation();
  const deepLinkParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawDate = params.get('date');
    const rawView = params.get('view');

    const deepLinkDate = rawDate
      ? (() => {
          const [year, month, day] = rawDate.split('-').map(Number);
          if (!year || !month || !day) return undefined;

          const parsedDate = new Date(year, month - 1, day);
          return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
        })()
      : undefined;

    return {
      deepLinkDate,
      deepLinkView: rawView ?? undefined,
    };
  }, [location.search]);
  const hasDeepLinkView = deepLinkParams.deepLinkView != null;
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const loginStateContext = useContext(LoginStateContext);
  const { avenyCollectionMap } = useContext(ModelContext);
  const rutinAvailabilityContext = useContext(RutinSettingsContext);
  const modelContext = React.useContext(ModelContext);
  const kodplanContext = useContext(KodplanContext);
  const dbName = activeDatabaseContext.activeDatabase;
  const [overView, setOverView] = useState(false);
  const [kalenderInitialUrvalQueryHasRun, setKalenderInitialUrvalQueryHasRun] =
    useState(false);
  const [seeMoreFeatures, setSeeMorefeatures] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [currentView, setCurrentView] = useState(
    deepLinkParams.deepLinkView ?? 'Week2'
  );
  const [currentDate, setCurrentDate] = useState<Date | undefined>(
    deepLinkParams.deepLinkDate
  );
  const [dayCount, setDayCount] = useState<number>(7);
  const [nyAktivitetInitialValues, setNyAktivitetInitialValues] = useState<
    INyAktivitetInitialValues | undefined
  >();
  const [calendarSelection, setCalendarSelection] = useState<
    ICalendarSelection | undefined
  >(undefined);
  const [resources, setResources] = useState<IResourceInfo[] | undefined>();
  const [loggedInUserResource, setLoggedInUserResource] = useState<
    IResourceInfo | undefined
  >();
  const [showOutlookBookings, setShowOutlookBookings] = useState(false);
  const [calenderReportSysOption, setCalenderReportSysOption] =
    useState<React.SetStateAction<string | undefined>>();
  const scrollToCurrentIndexRef = useRef<(() => void) | null>(null);

  const queryOptions = useMemo(
    () => ({
      variables: {
        dbName: dbName ?? '',
        section: [`Forms/${padRoutineId(rutinInfos.rtnWebKalender.rutinID)}`],
      },
      skip: dbName == null,
    }),
    [dbName]
  );

  const dbOverviewSettings = (data: any) => {
    const overviewDBSetting = (
      data?.me?.[0]?.dbsettings?.[0]?.values?.find(
        (value: { name: string }) => value.name === 'overview'
      )
        ?.value as KalenderDBSettingsQuery_me_dbsettings_values_value_TEDMixedBoolean
    )?.asBoolean;

    if (overviewDBSetting != null) {
      setOverView(overviewDBSetting);
    }
  };

  const dbOutlookSettings = (data: any) => {
    const outlookDBSetting = (
      data?.me?.[0]?.dbsettings?.[0]?.values?.find(
        (value: { name: string }) => value.name === 'showOutlookBookings'
      )
        ?.value as KalenderDBSettingsQuery_me_dbsettings_values_value_TEDMixedBoolean
    )?.asBoolean;

    if (outlookDBSetting != null) {
      setShowOutlookBookings(outlookDBSetting);
    }
  };

  // Returns a new view if the current view is old, otherwise returns the a new view
  const getView = (currentView: string): string => {
    if (
      currentView === 'Day2' ||
      currentView === 'Week2' ||
      currentView === 'Month2' ||
      currentView === 'WorkWeek' ||
      currentView === 'DayOverview' ||
      currentView === 'WeekOverview' ||
      currentView === 'WeekOverview2' ||
      currentView === 'List'
    ) {
      return currentView;
    }
    return 'Week2';
  };

  const dbCalendarViewSettings = (data: any) => {
    if (hasDeepLinkView) return;

    const viewDBSetting = (
      data?.me?.[0]?.dbsettings?.[0]?.values?.find(
        (value: { name: string }) => value.name === 'currentView'
      )
        ?.value as KalenderDBSettingsQuery_me_dbsettings_values_value_TEDMixedString
    )?.asString;

    if (viewDBSetting != null) {
      setCurrentView(getView(viewDBSetting));
    }
  };

  useEffect(() => {
    if (deepLinkParams.deepLinkDate != null) {
      setCurrentDate(deepLinkParams.deepLinkDate);
    }

    if (deepLinkParams.deepLinkView != null) {
      setCurrentView(getView(deepLinkParams.deepLinkView));
    }
  }, [deepLinkParams.deepLinkDate, deepLinkParams.deepLinkView]);

  const getCalendarReportSysOption = (
    data: AvenySYSOptionQuery | undefined,
    loadingData: boolean
  ) => {
    if (data != null) {
      setInitialized(true);
    }

    const opt_setting =
      !loadingData && data?.db?.SYSOptions_All?.[0]?.OptionData
        ? data?.db?.SYSOptions_All?.[0]?.OptionData
        : undefined;

    setCalenderReportSysOption(opt_setting);
  };

  const {
    data: calendar_report_sys_option,
    loading: calendar_report_sys_option_loading,
  } = useQuery<AvenySYSOptionQuery, AvenySYSOptionQueryVariables>(optionQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      dbName: activeDatabaseContext.activeDatabase ?? '',
      subSystemCode: 'BO',
      optionKey: 'BO_NA_CALENDAR_REPORT',
    },
    skip: !activeDatabaseContext.activeDatabase,
  });

  const kalenderDbSettingsQuery = useQuery<
    KalenderDBSettingsQuery,
    KalenderDBSettingsQueryVariables
  >(kalenderDBSettingsQuery, queryOptions);

  useEffect(() => {
    if (kalenderDbSettingsQuery.data != null && isInitializing) {
      dbOverviewSettings(kalenderDbSettingsQuery.data);
      dbOutlookSettings(kalenderDbSettingsQuery.data);
      dbCalendarViewSettings(kalenderDbSettingsQuery.data);

      setInitialized(true);
      setIsInitializing(false);
    }

    getCalendarReportSysOption(
      calendar_report_sys_option,
      calendar_report_sys_option_loading
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    kalenderDbSettingsQuery.data,
    calendar_report_sys_option,
    isInitializing,
  ]);

  const { loading: kalenderInitialUrvalQueryLoading } = useQuery<
    KalenderInitialUrvalQuery,
    KalenderInitialUrvalQueryVariables
  >(kalenderInitialUrvalQuery, {
    variables: {
      collection: 'BOResurs',
      dbname: dbName ?? '',
      routineID: rutinInfos.rtnWebKalender.rutinID,
      selectedBokningsgrupp: rutinAvailabilityContext.currentBookingGroup ?? '',
      kontoplanID: kodplanContext.activeKodplan?.id,
    },
    skip:
      !dbName ||
      kalenderInitialUrvalQueryHasRun ||
      kodplanContext.activeKodplan?.id == null,
    onCompleted: data => {
      if (data) {
        const UIUrval = convertUrval(
          data.routinedefaultselection,
          'BOResurs',
          modelContext.avenyCollectionMap
        );
        const resources = getCheckedResourcesFromUrval(UIUrval, undefined);
        const resourceInfo = convertResourceIDsToResourceInfos(
          resources,
          loginStateContext.sign,
          data
        );
        setResources(resourceInfo);
        setKalenderInitialUrvalQueryHasRun(true);
      }
    },
    onError: error => {
      Logger.error(error);
    },
    fetchPolicy: 'network-only',
  });

  const [updateKalenderDBSettings] = useMutation<
    updateKalenderDBSettings,
    updateKalenderDBSettingsVariables
  >(updateKalenderDBSettingsMutation, {
    updateQueries: {
      KalenderDBSettingsQuery: (prevResult, options) => {
        const previousResult = prevResult as KalenderDBSettingsQuery;
        if (!options.mutationResult.errors) {
          return {
            me: [
              {
                ...previousResult?.me?.[0],
                dbsettings: options.mutationResult.data?.savedbsettings,
              },
            ],
          } as KalenderDBSettingsQuery;
        }
        return previousResult;
      },
    },
  });

  // Updates Outlook bokings and calendar view
  useEffect(() => {
    if (
      dbName &&
      !kalenderDbSettingsQuery.loading &&
      initialized &&
      !isInitializing
    ) {
      updateKalenderDBSettings({
        variables: {
          dbName: dbName,
          section: [
            {
              name: `Forms/${padRoutineId(rutinInfos.rtnWebKalender.rutinID)}`,
              values: [
                { name: 'overview', asBoolean: overView },
                { name: 'showOutlookBookings', asBoolean: showOutlookBookings },
                { name: 'currentView', asString: currentView },
              ],
            },
          ],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dbName,
    overView,
    showOutlookBookings,
    currentView,
    updateKalenderDBSettings,
    initialized,
    isInitializing,
  ]);

  const getNyAktivitetInitialValues = useCallback(() => {
    if (nyAktivitetInitialValues) {
      setNyAktivitetInitialValues(undefined);
    }

    return nyAktivitetInitialValues;
  }, [nyAktivitetInitialValues, setNyAktivitetInitialValues]);

  useEffect(() => {
    if (
      setUrval &&
      !kalenderInitialUrvalQueryLoading &&
      kalenderInitialUrvalQueryHasRun
    ) {
      setUrval(
        makeSelection(
          resources,
          loginStateContext.sign,
          currentDate ?? defaultKalenderProps.currentDate,
          currentView ?? defaultKalenderProps.currentView,
          avenyCollectionMap
        )
      );
    }
  }, [
    avenyCollectionMap,
    currentDate,
    currentView,
    loginStateContext.sign,
    setUrval,
    resources,
    kalenderInitialUrvalQueryLoading,
    kalenderInitialUrvalQueryHasRun,
  ]);

  return (
    <ResursTypContextProvider>
      <ViewDbSettingsContextProvider
        currentView={currentView}
        overView={overView}
        setCurrentView={setCurrentView}
        setOverView={setOverView}
        seeMoreFeatures={seeMoreFeatures}
        setSeeMoreFeatures={setSeeMorefeatures}
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        resources={resources}
        setResources={setResources}
        loggedInUserResource={loggedInUserResource}
        setLoggedInUserResource={setLoggedInUserResource}
        nyAktivitetInitialValues={nyAktivitetInitialValues}
        getNyAktivitetInitialValues={getNyAktivitetInitialValues}
        setNyAktivitetInitialValues={setNyAktivitetInitialValues}
        calendarSelection={calendarSelection}
        setCalendarSelection={setCalendarSelection}
        showOutlookBookings={showOutlookBookings}
        setShowOutlookBookings={setShowOutlookBookings}
        setDayCount={setDayCount}
        dayCount={dayCount}
        calenderReportSysOption={calenderReportSysOption}
        setCalenderReportSysOption={setCalenderReportSysOption}
        scrollToCurrentIndexRef={scrollToCurrentIndexRef}
      >
        {props.children}
      </ViewDbSettingsContextProvider>
    </ResursTypContextProvider>
  );
};

export default KalenderWrapper;
